// Expanding a Schedule's recurring blocks into the occurrences that fall inside a
// window.
//
// A block carries an iCalendar RRULE. This understands the subset the app can
// actually produce — FREQ=DAILY|WEEKLY|MONTHLY, INTERVAL, BYDAY, COUNT, UNTIL — and
// ignores the rest of the standard rather than pretending to implement it. An
// unsupported FREQ yields the anchor occurrence alone, so a schedule can never
// silently vanish from the timeline.

import type { ScheduleInput } from "@/types";
import type { Range } from "./timeScale";

type Block = ScheduleInput["blocks"][number];

export type Occurrence = { startMs: number; endMs: number };

/** Runaway guard: a malformed rule must not spin forever. */
const MAX_OCCURRENCES = 500;
const MINUTE_MS = 60_000;
const WEEKDAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;

/** Every occurrence of a block that overlaps the window. */
export function expandBlock(block: Block, range: Range): Occurrence[] {
  const anchor = new Date(block.dtStart);
  const durationMs = block.durationMinutes * MINUTE_MS;
  if (!Number.isFinite(anchor.getTime())) return [];

  const rule = parseRule(block.rrule);
  const interval = Math.max(1, Number(rule.get("INTERVAL") ?? 1));
  const until = rule.has("UNTIL") ? parseUntil(rule.get("UNTIL")) : null;
  const count = rule.has("COUNT") ? Number(rule.get("COUNT")) : null;
  const byDay = parseByDay(rule.get("BYDAY"));

  const starts = startsFor(rule.get("FREQ"), anchor, range, interval, byDay);

  const occurrences: Occurrence[] = [];
  for (const startMs of starts) {
    if (count !== null && occurrences.length >= count) break;
    if (until !== null && startMs > until) break;
    const endMs = startMs + durationMs;
    if (endMs >= range.startMs && startMs <= range.endMs) occurrences.push({ startMs, endMs });
  }
  return occurrences;
}

/** Candidate start times from the anchor up to the end of the window. */
function startsFor(
  freq: string | undefined,
  anchor: Date,
  range: Range,
  interval: number,
  byDay: Set<number>,
): number[] {
  switch (freq) {
    case "DAILY":
      return step(anchor, range, (date) => addDays(date, interval));
    case "WEEKLY":
      return byDay.size === 0
        ? step(anchor, range, (date) => addDays(date, 7 * interval))
        : weeklyByDay(anchor, range, interval, byDay);
    case "MONTHLY":
      return step(anchor, range, (date) => addMonths(date, interval));
    default:
      // An unsupported rule still shows its anchor, rather than disappearing.
      return [anchor.getTime()];
  }
}

/** Walk from the anchor by a fixed step until past the window. */
function step(anchor: Date, range: Range, next: (date: Date) => Date): number[] {
  const starts: number[] = [];
  let cursor = new Date(anchor);
  for (let i = 0; i < MAX_OCCURRENCES && cursor.getTime() <= range.endMs; i += 1) {
    starts.push(cursor.getTime());
    cursor = next(cursor);
  }
  return starts;
}

/** WEEKLY with BYDAY: every named weekday, in every interval-th week. */
function weeklyByDay(anchor: Date, range: Range, interval: number, byDay: Set<number>): number[] {
  const starts: number[] = [];
  let weekStart = startOfWeek(anchor);

  for (let i = 0; i < MAX_OCCURRENCES && weekStart.getTime() <= range.endMs; i += 1) {
    for (const weekday of byDay) {
      const at = addDays(weekStart, (weekday + 6) % 7); // weeks start Monday
      at.setHours(anchor.getHours(), anchor.getMinutes(), 0, 0);
      if (at.getTime() >= anchor.getTime()) starts.push(at.getTime());
    }
    weekStart = addDays(weekStart, 7 * interval);
  }
  return starts.sort((a, b) => a - b);
}

function parseRule(rrule: string): Map<string, string> {
  const parts = rrule
    .replace(/^RRULE:/i, "")
    .split(";")
    .map((part) => part.split("="));
  return new Map(parts.map(([key, value]) => [(key ?? "").toUpperCase(), value ?? ""]));
}

function parseByDay(value: string | undefined): Set<number> {
  if (!value) return new Set();
  const days = value
    .split(",")
    .map((day) => WEEKDAYS.indexOf(day.trim().toUpperCase().slice(-2) as (typeof WEEKDAYS)[number]))
    .filter((index) => index >= 0);
  return new Set(days);
}

function parseUntil(value: string | undefined): number | null {
  if (!value) return null;
  // Both the basic iCalendar form (20261231T235959Z) and plain ISO are accepted.
  const iso = /^\d{8}T\d{6}Z$/.test(value)
    ? `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}T${value.slice(
        9,
        11,
      )}:${value.slice(11, 13)}:${value.slice(13, 15)}Z`
    : value;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addMonths(date: Date, months: number): Date {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
}

function startOfWeek(date: Date): Date {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return addDays(start, -((start.getDay() + 6) % 7));
}
